// 头像 —— 没有 image 时用 sender id 首 2 字母 + 灰底
// 圆形，32px / 24px / 40px 三档

import { useMemo, useState } from "react";

import { inferAuthBase } from "../lib/auth-api";

interface AvatarProps {
  /** sender id 或 device id，用于派生首字母 + 配色 */
  seed: string;
  /** 显示文本（如 sender label）；不传则用 seed */
  label?: string;
  size?: 24 | 32 | 40 | 64;
  /** 右下角小状态点：online/offline；不传不显示 */
  presence?: "online" | "offline";
  /** K4: 头像图片 URL，有就优先于 initial fallback */
  src?: string | null;
}

export function Avatar({ seed, label, size = 32, presence, src }: AvatarProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const initial = useMemo(() => {
    const s = (label ?? seed).trim();
    if (!s) return "?";
    const chars = Array.from(s);
    return chars.slice(0, 2).join("").toUpperCase();
  }, [seed, label]);

  const bg = useMemo(() => hashColor(seed), [seed]);
  const imageSrc = useMemo(() => resolveAvatarSrc(src), [src]);

  if (imageSrc && imageSrc !== failedSrc) {
    return (
      <span
        className="avatar avatar--img"
        style={{ width: size, height: size }}
        data-presence={presence}
        aria-label={label ?? seed}
      >
        <img
          src={imageSrc}
          alt=""
          width={size}
          height={size}
          draggable={false}
          onError={() => setFailedSrc(imageSrc)}
        />
      </span>
    );
  }

  return (
    <span
      className="avatar"
      style={{
        width: size,
        height: size,
        fontSize: size === 24 ? 10 : size === 32 ? 12 : size === 40 ? 14 : 22,
        background: bg,
      }}
      data-presence={presence}
      aria-label={label ?? seed}
    >
      {initial}
    </span>
  );
}

function resolveAvatarSrc(src?: string | null): string | null {
  if (!src) return null;
  if (src.startsWith("http://") || src.startsWith("https://") || src.startsWith("data:")) {
    return src;
  }
  if (src.startsWith("/")) {
    return `${inferAuthBase()}${src}`;
  }
  return src;
}

function hashColor(seed: string): string {
  // 黑白主题下，用灰阶变化保持视觉一致
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const shade = 110 + (Math.abs(h) % 80); // 110-189 灰度
  return `rgb(${shade}, ${shade}, ${shade})`;
}
